<template>
  <div class="v2-affiliate-new v2-container v2-section">
    <nav class="v2-affiliate-new__back" aria-label="Volver">
      <BaseButton variant="ghost" size="sm" :to="backUrl">
        <template #icon-left>
          <v-icon size="16">mdi-chevron-left</v-icon>
        </template>
        Volver a submiembros
      </BaseButton>
    </nav>

    <header class="v2-affiliate-new__header">
      <div>
        <p class="v2-overline">{{ isEditing ? "Editar" : "Membresía" }}</p>
        <h1 class="v2-headline v2-headline--display-lg v2-affiliate-new__title">
          {{ isEditing ? "Editar submiembro" : "Agregar submiembro" }}
        </h1>
        <p class="v2-body v2-body--md v2-body--muted">
          {{
            isEditing
              ? "Actualiza los datos del submiembro vinculado a tu membresía."
              : "Completa los datos para vincular un nuevo miembro a tu cuenta."
          }}
        </p>
      </div>
    </header>

    <div v-if="loadingAffiliate" class="v2-affiliate-new__state">
      <v-icon size="32" color="grey lighten-1">mdi-progress-clock</v-icon>
      <p class="v2-body v2-body--sm v2-body--muted">Cargando datos…</p>
    </div>

    <div v-else class="v2-affiliate-new__grid">
      <form class="v2-affiliate-new__form" @submit.prevent="onSubmit">
        <MemberCard>
          <template #header>
            <div class="v2-affiliate-new__form-header">
              <div class="v2-affiliate-new__form-icon" aria-hidden="true">
                <v-icon size="22" color="primary">mdi-account-plus-outline</v-icon>
              </div>
              <div>
                <h3 class="v2-headline v2-headline--display-sm">
                  Datos del nuevo miembro
                </h3>
                <p class="v2-body v2-body--sm v2-body--muted">
                  Los campos marcados con * son obligatorios.
                </p>
              </div>
            </div>
          </template>

          <div class="v2-affiliate-new__row">
            <BaseInput
              v-model="form.user.name"
              label="Nombre(s)"
              placeholder="Ej. Ricardo"
              required
              :error="errors.name"
            />
            <BaseInput
              v-model="form.user.firstLastname"
              label="Apellidos"
              placeholder="Ej. Guzmán Ortiz"
              required
              :error="errors.firstLastname"
            />
          </div>

          <BaseInput
            v-model="form.user.email"
            type="email"
            label="Correo electrónico"
            placeholder="correo@ejemplo.com"
            required
            :error="errors.email"
            :disabled="isEditing"
            helper="Será el identificador único del submiembro. No se puede modificar al editar."
          />

          <template v-if="customFieldGroups.length > 0">
            <div class="v2-affiliate-new__divider">
              <span class="v2-affiliate-new__divider-label">
                Campos adicionales
              </span>
            </div>

            <template v-for="(group, gi) in customFieldGroups">
              <div
                v-if="group.title"
                :key="`title-${gi}`"
                class="v2-affiliate-new__group-title"
              >
                {{ group.title }}
              </div>
              <div
                v-for="field in group.fields"
                :key="`${gi}-${field._id || field.formattedName}`"
                class="v2-affiliate-new__field"
              >
                <FormFieldRenderer
                  :field="field"
                  :value="customFieldsData[field.formattedName]"
                  :error="errors[field.formattedName]"
                  @update="onFieldUpdate(field.formattedName, $event)"
                />
              </div>
            </template>
          </template>

          <template #footer>
            <div class="v2-affiliate-new__actions">
              <BaseButton variant="secondary" :to="backUrl" type="button">
                Cancelar
              </BaseButton>
              <BaseButton
                variant="primary"
                type="submit"
                :loading="submitting"
                :disabled="submitting"
              >
                <template #icon-left>
                  <v-icon size="18">
                    {{ isEditing ? "mdi-content-save" : "mdi-content-save-outline" }}
                  </v-icon>
                </template>
                {{ isEditing ? "Guardar cambios" : "Crear submiembro" }}
              </BaseButton>
            </div>
          </template>
        </MemberCard>
      </form>

      <aside class="v2-affiliate-new__sidebar">
        <section class="v2-affiliate-new__tip">
          <h4 class="v2-affiliate-new__tip-title">
            <v-icon size="18" color="amber">mdi-lightbulb-on-outline</v-icon>
            Antes de continuar
          </h4>
          <ul class="v2-affiliate-new__tip-list">
            <li>
              <v-icon size="14" color="amber">mdi-check-circle</v-icon>
              <span>
                Verifica que el correo sea válido y de uso personal del
                submiembro.
              </span>
            </li>
            <li>
              <v-icon size="14" color="amber">mdi-check-circle</v-icon>
              <span>
                El submiembro recibirá un correo de bienvenida con las
                instrucciones para activar su cuenta.
              </span>
            </li>
            <li>
              <v-icon size="14" color="amber">mdi-check-circle</v-icon>
              <span>
                Los beneficios de la membresía se activarán al confirmar el
                pago, si tu afiliación lo requiere.
              </span>
            </li>
          </ul>
        </section>

        <section class="v2-affiliate-new__summary">
          <h4 class="v2-affiliate-new__summary-title">Resumen de solicitud</h4>
          <dl class="v2-affiliate-new__summary-list">
            <div class="v2-affiliate-new__summary-row">
              <dt>Tipo</dt>
              <dd>
                <StatusBadge
                  :label="isEditing ? 'Edición' : 'Nuevo'"
                  :variant="isEditing ? 'info' : 'warning'"
                />
              </dd>
            </div>
            <div class="v2-affiliate-new__summary-row">
              <dt>Campos personalizados</dt>
              <dd>{{ customFieldGroups.length }} sección(es)</dd>
            </div>
            <div class="v2-affiliate-new__summary-row">
              <dt>Total de submiembros</dt>
              <dd>{{ currentTotal }}</dd>
            </div>
            <div v-if="limitEnforced" class="v2-affiliate-new__summary-row">
              <dt>Límite permitido</dt>
              <dd>{{ submemberLimitValue }}</dd>
            </div>
          </dl>
        </section>
      </aside>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import BaseButton from "@/components/memberDashboardV2/ui/BaseButton.vue";
import BaseInput from "@/components/memberDashboardV2/ui/BaseInput.vue";
import MemberCard from "@/components/memberDashboardV2/ui/MemberCard.vue";
import StatusBadge from "@/components/memberDashboardV2/ui/StatusBadge.vue";
import FormFieldRenderer from "./components/FormFieldRenderer.vue";
import { validations } from "@/share/validations.js";
import { groupFieldsBySection, buildMemberDataFromDefs } from "@/utils";
import { resolveMemberAffiliateBranch } from "@/common/memberDashboardRouting";

const EMAIL_RULES = validations.emailValidation({
  name: "correo electrónico",
  required: true,
});

const isValidEmail = (value) =>
  EMAIL_RULES.every((rule) => rule(value || "") === true);

export default {
  name: "MemberDashboardV2AffiliateNew",
  components: {
    BaseButton,
    BaseInput,
    MemberCard,
    StatusBadge,
    FormFieldRenderer,
  },
  data() {
    return {
      form: {
        user: {
          name: "",
          firstLastname: "",
          email: "",
        },
        customFields: {},
      },
      customFieldsData: {},
      memberFields: [],
      formSections: [],
      errors: {},
      loadingConfig: false,
      loadingAffiliate: false,
      submitting: false,
      currentTotal: 0,
      editingId: null,
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    ...mapState("membershipProfile", ["userMembership"]),
    backUrl() {
      return "/member-v2/affiliates";
    },
    isEditing() {
      return Boolean(this.editingId);
    },
    customFieldGroups() {
      return groupFieldsBySection(this.formSections, this.memberFields).filter(
        (group) => group.fields && group.fields.length > 0
      );
    },
    submemberLimit() {
      const details = this.user?.affiliation?.national?.details;
      if (!Array.isArray(details) || !details.length) return null;
      return details[0]?.affiliationType?.submembersLimit || null;
    },
    limitEnforced() {
      return Boolean(this.submemberLimit && this.submemberLimit.active);
    },
    submemberLimitValue() {
      return this.submemberLimit?.value || 0;
    },
  },
  methods: {
    ...mapActions("memberAffiliates", [
      "getAffiliates",
      "addAffiliate",
      "updateAffiliate",
    ]),
    ...mapActions("registrationFields", ["getSubMemberFormFields"]),
    ...mapActions("formSections", ["getFormSectionsByOrg"]),
    ...mapActions("user", ["realoadUserData"]),
    ...mapMutations("loader", ["loading", "loaded"]),
    ...mapMutations("notification", ["show"]),
    orgId() {
      return this.user?.headOffice?._id || this.user?.headOffice;
    },
    branchId() {
      return resolveMemberAffiliateBranch(this.user, this.userMembership);
    },
    onFieldUpdate(formattedName, value) {
      this.$set(this.customFieldsData, formattedName, value);
      if (this.errors[formattedName]) {
        const next = { ...this.errors };
        delete next[formattedName];
        this.errors = next;
      }
    },
    validate() {
      const errs = {};
      if (!this.form.user.name || !this.form.user.name.trim()) {
        errs.name = "El nombre es obligatorio";
      }
      if (!this.form.user.firstLastname || !this.form.user.firstLastname.trim()) {
        errs.firstLastname = "Los apellidos son obligatorios";
      }
      if (!this.form.user.email || !this.form.user.email.trim()) {
        errs.email = "El correo es obligatorio";
      } else if (!isValidEmail(this.form.user.email)) {
        errs.email = "Ingresa un correo válido";
      }

      this.customFieldGroups.forEach((group) => {
        (group.fields || []).forEach((field) => {
          if (!field.required) return;
          const value = this.customFieldsData[field.formattedName];
          if (
            value === undefined ||
            value === null ||
            value === "" ||
            (Array.isArray(value) && value.length === 0)
          ) {
            errs[field.formattedName] = "Este campo es obligatorio";
          }
        });
      });

      this.errors = errs;
      return Object.keys(errs).length === 0;
    },
    buildCustomFields() {
      const customFields = {};
      this.memberFields.forEach((field) => {
        const val = this.customFieldsData[field.formattedName];
        if (val !== undefined && val !== null && val !== "") {
          customFields[field.formattedName] = val;
        }
      });
      return customFields;
    },
    syncCustomFieldsToMemberData() {
      const user = this.form.user;
      if (!user || !Array.isArray(user.memberData)) return;
      user.memberData.forEach((entry) => {
        if (entry.fieldType === "DOC") return;
        if (
          Object.prototype.hasOwnProperty.call(
            this.customFieldsData,
            entry.formattedName
          )
        ) {
          entry.value = this.customFieldsData[entry.formattedName];
        }
      });
    },
    async loadOrgConfig() {
      this.loadingConfig = true;
      try {
        const orgId = this.orgId();
        if (!orgId) return;
        const [fieldsRes, sectionsRes] = await Promise.all([
          this.getSubMemberFormFields(orgId),
          this.getFormSectionsByOrg(orgId),
        ]);
        if (fieldsRes && fieldsRes.status) {
          this.memberFields = (fieldsRes.data && fieldsRes.data.registrationFields) || [];
        }
        if (sectionsRes && sectionsRes.status) {
          this.formSections = (sectionsRes.data && sectionsRes.data.sections) || [];
        }
      } catch (e) {
        this.show({
          text: "No se pudieron cargar los campos personalizados.",
          color: "warning",
        });
      } finally {
        this.loadingConfig = false;
      }
    },
    async loadAffiliateForEdit(id) {
      this.loadingAffiliate = true;
      try {
        const pageSize = 100;
        let page = 1;
        let totalPages = 1;
        let target = null;
        let lastTotalDocs = 0;
        let pagesScanned = 0;

        while (page <= totalPages && !target) {
          const response = await this.getAffiliates({
            page,
            limit: pageSize,
            name: "",
            validationStatus: "",
          });
          if (!response || !response.status || !response.affiliates) break;

          const data = response.affiliates.data || {};
          totalPages = data.totalPages || 1;
          lastTotalDocs = data.totalDocs || 0;
          const members = data.members || [];
          target = members.find((m) => (m._id || m.id) === id);
          pagesScanned++;
          page++;
        }

        if (target) {
          this.form.user = {
            _id: target._id,
            name: target.name || "",
            firstLastname: target.firstLastname || "",
            email: target.email || "",
          };
          // Materializa una fila por cada definición activa del org, mezclada
          // con el valor almacenado cuando existe. Garantiza que los campos
          // añadidos por el admin DESPUÉS de crear el submiembro tengan una
          // entrada donde syncCustomFieldsToMemberData pueda escribir.
          this.form.user.memberData = buildMemberDataFromDefs(
            Array.isArray(target.memberData) ? target.memberData : [],
            this.memberFields
          );
          this.form.user.memberData.forEach((entry) => {
            if (entry.fieldType === "DOC") return;
            this.$set(
              this.customFieldsData,
              entry.formattedName,
              entry.value
            );
          });
          this.currentTotal = lastTotalDocs;
        } else {
          this.show({
            text: "No se encontró el submiembro a editar.",
            color: "error",
          });
          this.$router.replace(this.backUrl);
        }
      } catch (e) {
        this.show({
          text: "No se pudo cargar el submiembro.",
          color: "error",
        });
        this.$router.replace(this.backUrl);
      } finally {
        this.loadingAffiliate = false;
      }
    },
    async guardSubmemberLimit() {
      // Misma regla que el backend al crear: solo aplica si el límite está
      // activo y tiene un valor mayor a 0.
      if (!this.limitEnforced || !(this.submemberLimitValue > 0)) return;
      try {
        const response = await this.getAffiliates({
          page: 1,
          limit: 1,
          name: "",
          validationStatus: "",
          branch: "",
        });
        const data =
          (response && response.affiliates && response.affiliates.data) || {};
        const total = data.totalSubmembers ?? data.totalDocs ?? 0;
        if (total >= this.submemberLimitValue) {
          this.show({
            text: `Has alcanzado el límite de ${this.submemberLimitValue} ${
              this.submemberLimitValue === 1 ? "submiembro" : "submiembros"
            } permitidos para tu afiliación.`,
            color: "warning",
          });
          this.$router.replace(this.backUrl);
        }
      } catch (e) {
        // Si la consulta falla, el backend sigue validando el límite al crear.
      }
    },
    async onSubmit() {
      if (this.submitting) return;
      if (!this.validate()) {
        this.show({
          text: "Revisa los campos del formulario",
          color: "error",
        });
        return;
      }
      this.submitting = true;
      this.loading();
      try {
        if (this.isEditing) {
          this.syncCustomFieldsToMemberData();
          delete this.form.customFields;
        } else {
          this.form.customFields = this.buildCustomFields();
        }
        if (this.isEditing) {
          const updated = await this.updateAffiliate(this.form);
          if (updated && updated.status) {
            this.show({
              text: "Submiembro actualizado correctamente",
              color: "success",
            });
            if (this.user && this.user.email) {
              try {
                await this.realoadUserData({ email: this.user.email });
              } catch (e) {
                // silent
              }
            }
            this.$router.replace(this.backUrl);
          } else {
            this.show({
              text: (updated && updated.message) || "Error al actualizar el submiembro",
              color: "error",
            });
          }
        } else {
          const created = await this.addAffiliate({
            affiliate: this.form,
            organization: this.orgId(),
            branch: this.branchId(),
          });
          if (created && created.status) {
            this.show({
              text: "Submiembro/Colaborador creado exitosamente",
              color: "success",
            });
            if (this.user && this.user.email) {
              try {
                await this.realoadUserData({ email: this.user.email });
              } catch (e) {
                // silent
              }
            }
            this.$router.replace(this.backUrl);
          } else {
            this.show({
              text: (created && created.message) || "Error al crear el submiembro",
              color: "error",
            });
          }
        }
      } catch (e) {
        this.show({
          text: "Ocurrió un error inesperado. Intenta nuevamente.",
          color: "error",
        });
      } finally {
        this.loaded();
        this.submitting = false;
      }
    },
  },
  async mounted() {
    const editId = this.$route && this.$route.query && this.$route.query.edit;
    if (editId) {
      this.editingId = editId;
    }
    await this.loadOrgConfig();
    if (this.editingId) {
      await this.loadAffiliateForEdit(this.editingId);
    } else {
      await this.guardSubmemberLimit();
    }
  },
};
</script>

<style scoped>
.v2-affiliate-new {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
}

.v2-affiliate-new__back {
  display: flex;
  align-items: center;
}

.v2-affiliate-new__header {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.v2-affiliate-new__title {
  margin: 0;
}

.v2-affiliate-new__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-10) var(--space-4);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
}

.v2-affiliate-new__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-6);
  align-items: flex-start;
}

@media (min-width: 1024px) {
  .v2-affiliate-new__grid {
    grid-template-columns: minmax(0, 7fr) minmax(0, 3fr);
  }
}

.v2-affiliate-new__form {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.v2-affiliate-new__form-header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-width: 0;
}

.v2-affiliate-new__form-icon {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  background-color: var(--color-primary-soft);
  flex-shrink: 0;
}

.v2-affiliate-new__row {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4);
}

@media (min-width: 600px) {
  .v2-affiliate-new__row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.v2-affiliate-new__divider {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin: var(--space-6) 0 var(--space-2) 0;
  color: var(--color-text-muted);
}

.v2-affiliate-new__divider::before,
.v2-affiliate-new__divider::after {
  content: "";
  flex: 1 1 auto;
  height: 1px;
  background-color: var(--color-border);
}

.v2-affiliate-new__divider-label {
  font-size: var(--fs-label-sm);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  font-weight: var(--fw-semibold);
}

.v2-affiliate-new__group-title {
  font-family: var(--font-body);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  color: var(--color-primary);
  margin: var(--space-4) 0 var(--space-1) 0;
  padding-bottom: var(--space-1);
  border-bottom: 1px solid var(--color-border);
}

.v2-affiliate-new__field {
  margin-top: var(--space-3);
}

.v2-affiliate-new__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2);
  flex-wrap: wrap;
  width: 100%;
}

.v2-affiliate-new__sidebar {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-width: 0;
}

.v2-affiliate-new__tip {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-5);
  background-color: var(--color-amber-soft-2);
  border: 1px solid var(--color-amber-soft);
  border-radius: var(--radius-lg);
}

.v2-affiliate-new__tip-title {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-body);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  color: var(--color-amber-on);
  margin: 0;
}

.v2-affiliate-new__tip-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.v2-affiliate-new__tip-list li {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  font-size: var(--fs-body-sm);
  color: var(--color-amber-on);
  line-height: var(--lh-snug);
}

.v2-affiliate-new__summary {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-5);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xs);
}

.v2-affiliate-new__summary-title {
  font-family: var(--font-body);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  color: var(--color-text);
  margin: 0;
}

.v2-affiliate-new__summary-list {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.v2-affiliate-new__summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-2) 0;
  border-bottom: 1px solid var(--color-border);
  font-size: var(--fs-body-sm);
}

.v2-affiliate-new__summary-row:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.v2-affiliate-new__summary-row dt {
  color: var(--color-text-muted);
  margin: 0;
}

.v2-affiliate-new__summary-row dd {
  margin: 0;
  color: var(--color-text);
  font-weight: var(--fw-semibold);
  text-align: right;
}
</style>
